import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMinistry, addAdjustment, deleteAdjustment, deleteReimbursement, getAssetCount, updateMinistryLogo } from '../services/api';
import { formatAccountingCurrency, formatDate } from '../utils/formatters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWallet, 
  faCheckCircle, 
  faFileInvoiceDollar, 
  faPlusCircle, 
  faTrash, 
  faCalendar,
  faTimesCircle,
  faClock,
  faMoneyBillWave,
  faExclamationCircle,
  faBoxes,
  faArrowRight,
  faBell,
  faCamera,
  faSearchPlus,
  faSearchMinus,
  faCropAlt,
  faClipboard,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';
import './MinistryDetail.css';

const MinistryDetail = ({ selectedYear }) => {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [ministry, setMinistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({
    amount: '',
    description: '',
    type: 'add'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assetInfo, setAssetInfo] = useState({ count: 0, pendingCount: 0 });

  // Logo crop states
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    loadMinistry();
    loadAssetCount();
  }, [id, selectedYear]);

  const loadMinistry = async () => {
    try {
      setLoading(true);
      const response = await getMinistry(id, selectedYear);
      setMinistry(response.data);
    } catch (error) {
      console.error('Erro ao carregar ministério:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAssetCount = async () => {
    try {
      const response = await getAssetCount(id);
      setAssetInfo(response.data);
    } catch (error) {
      console.error('Erro ao carregar contagem de patrimônio:', error);
    }
  };

  const handleAdjustment = async (e) => {
    e.preventDefault();
    try {
      const amount = adjustmentData.type === 'add' 
        ? parseFloat(adjustmentData.amount)
        : -parseFloat(adjustmentData.amount);

      await addAdjustment(id, {
        amount,
        description: adjustmentData.description,
        year: selectedYear
      });

      setShowAdjustmentForm(false);
      setAdjustmentData({ amount: '', description: '', type: 'add' });
      loadMinistry();
    } catch (error) {
      console.error('Erro ao adicionar ajuste:', error);
      alert('Erro ao adicionar ajuste');
    }
  };

  const handleOpenDeleteModal = (type, itemId, description) => {
    setDeleteTarget({ type, id: itemId, description });
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'adjustment') {
        await deleteAdjustment(id, deleteTarget.id);
        alert('Ajuste removido com sucesso!');
      } else if (deleteTarget.type === 'reimbursement') {
        await deleteReimbursement(deleteTarget.id);
        alert('Reembolso removido com sucesso!');
      }
      
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadMinistry();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      alert(error.response?.data?.message || 'Erro ao deletar item.');
    }
  };

  // ===== Logo Crop Functions =====
  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const readFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result));
      reader.readAsDataURL(file);
    });
  };

  const handleLogoClick = () => {
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
      logoInputRef.current.click();
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.warn('A imagem deve ter no máximo 5MB.');
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.warn('Formato não suportado. Use PNG, JPG, WEBP ou SVG.');
      return;
    }
    const dataUrl = await readFile(file);
    setImageSrc(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setShowCropModal(true);
  };

  const handlePasteImage = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          if (file.size > 5 * 1024 * 1024) {
            toast.warn('A imagem colada deve ter no máximo 5MB.');
            return;
          }
          const dataUrl = await readFile(file);
          setImageSrc(dataUrl);
          setCrop({ x: 0, y: 0 });
          setZoom(1);
          setShowCropModal(true);
        }
        return;
      }
    }
  }, []);

  useEffect(() => {
    if (showCropModal) {
      document.addEventListener('paste', handlePasteImage);
      return () => document.removeEventListener('paste', handlePasteImage);
    }
  }, [showCropModal, handlePasteImage]);

  const getCroppedImg = async (imageSrcUrl, pixelCrop) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = imageSrcUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, 256, 256);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95);
    });
  };

  const handleCropConfirm = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    try {
      setUploadingLogo(true);
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append('logo', croppedBlob, 'logo.png');
      const response = await updateMinistryLogo(id, formData);
      setMinistry(prev => ({ ...prev, logoUrl: response.data.logoUrl }));
      setShowCropModal(false);
      setImageSrc(null);
      toast.success('Logo atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar logo:', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar o logo.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  if (!ministry) {
    return <div className="container"><div className="card">Ministério não encontrado</div></div>;
  }

  const { yearlyBalance, reimbursements } = ministry;
  const isCurrentYear = selectedYear === new Date().getFullYear();
  const approvedReimbursements = reimbursements?.filter(r => 
    r.status === 'approved' || r.status === 'paid'
  ) || [];

  // Simplified view for non-admin users
  if (!isAdmin) {
    return (
      <div className="ministry-detail-page">
        <div className="container">
          <div className="ministry-header">
            <div className="ministry-header-title">
              <div className="logo-upload-wrapper" onClick={handleLogoClick} title="Clique para alterar o logo do ministério">
                {uploadingLogo ? (
                  <span className="ministry-detail-logo-fallback logo-uploading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </span>
                ) : ministry.logoUrl ? (
                  <img src={ministry.logoUrl} alt="" className="ministry-detail-logo" />
                ) : (
                  <span className="ministry-detail-logo-fallback">
                    <i className="fas fa-church"></i>
                  </span>
                )}
                <div className="logo-upload-overlay">
                  <FontAwesomeIcon icon={faCamera} />
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  onChange={handleLogoFileChange}
                  style={{ display: 'none' }}
                />
              </div>
              <div>
                <h1>{ministry.name} - {selectedYear}</h1>
                <p className="ministry-leader-info">
                  Líder: {ministry.leader.name} - CPF/CNPJ: {ministry.leader.cpfCnpj}
                </p>
              </div>
            </div>
          </div>

          <div className="ministry-content">
            {/* Balance Card */}
            <div className="card user-balance-card">
              <div className="balance-display">
                <FontAwesomeIcon icon={faWallet} className="balance-icon" />
                <div className="balance-info">
                  <h3>Saldo Disponível</h3>
                  <div className={`balance-amount ${yearlyBalance.currentBalance < 0 ? 'text-negative' : 'text-positive'}`}>
                    {formatAccountingCurrency(yearlyBalance.currentBalance)}
                  </div>
                </div>
              </div>
            </div>

            {/* Patrimônio Card */}
            <Link to={`/ministry/${id}/assets`} className="patrimonio-card">
              <div className="patrimonio-card-icon">
                <FontAwesomeIcon icon={faBoxes} />
              </div>
              <div className="patrimonio-card-info">
                <h3>Patrimônio</h3>
                <p>{assetInfo.count} {assetInfo.count === 1 ? 'bem cadastrado' : 'bens cadastrados'}</p>
              </div>
              {assetInfo.pendingCount > 0 && (
                <div className="patrimonio-card-badge">
                  <FontAwesomeIcon icon={faBell} style={{ marginRight: '4px' }} />
                  {assetInfo.pendingCount} pendente{assetInfo.pendingCount > 1 ? 's' : ''}
                </div>
              )}
              <FontAwesomeIcon icon={faArrowRight} className="patrimonio-card-arrow" />
            </Link>

            {/* Approved Reimbursements Card */}
            {isCurrentYear && (
              <div className="card">
                <div className="reimbursement-header">
                  <h2>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px', color: '#28a745' }} />
                    Reembolsos Aprovados {selectedYear}
                  </h2>
                  <button 
                    onClick={() => navigate(`/ministry/${id}/request-reimbursement`)}
                    className="btn btn-primary btn-sm"
                  >
                    <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '6px' }} />
                    Solicitar Reembolso
                  </button>
                </div>

                {approvedReimbursements.length > 0 ? (
                  <div className="approved-reimbursements-grid">
                    {approvedReimbursements.map((reimb) => (
                      <div key={reimb._id} className="approved-reimbursement-card">
                        <div className="reimbursement-card-header">
                          <span className={`badge badge-${reimb.status}`}>
                            <FontAwesomeIcon 
                              icon={reimb.status === 'paid' ? faCheckCircle : faClock} 
                              style={{ marginRight: '4px' }} 
                            />
                            {reimb.status === 'approved' && 'Aprovado'}
                            {reimb.status === 'paid' && 'Pago'}
                          </span>
                          <span className="reimbursement-date">
                            <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '4px' }} />
                            {formatDate(reimb.createdAt)}
                          </span>
                        </div>
                        <p className="reimbursement-description">{reimb.description}</p>
                        <div className="reimbursement-amount">
                          <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '6px' }} />
                          <strong>{formatAccountingCurrency(reimb.amount)}</strong>
                        </div>
                        {reimb.status === 'paid' && reimb.paymentProofUrl && (
                          <a
                            href={reimb.paymentProofUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-success"
                            style={{ marginTop: '12px' }}
                          >
                            <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '6px' }} />
                            Ver Comprovante de Pagamento
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center" style={{ padding: '20px' }}>
                    Nenhum reembolso aprovado neste ano.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Crop Modal */}
          {showCropModal && imageSrc && (
            <div className="modal-overlay crop-modal-overlay" onClick={handleCropCancel}>
              <div className="modal-content crop-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>
                    <FontAwesomeIcon icon={faCropAlt} style={{ marginRight: '8px' }} />
                    Ajustar Logo
                  </h2>
                  <button className="modal-close" onClick={handleCropCancel}>×</button>
                </div>
                <div className="modal-body crop-modal-body">
                  <div className="crop-hint">
                    <FontAwesomeIcon icon={faCamera} style={{ marginRight: '6px' }} />
                    Arraste para posicionar e use o zoom para ajustar
                  </div>
                  <div className="crop-container">
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div className="crop-controls">
                    <FontAwesomeIcon icon={faSearchMinus} className="crop-zoom-icon" />
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.05}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="crop-zoom-slider"
                    />
                    <FontAwesomeIcon icon={faSearchPlus} className="crop-zoom-icon" />
                  </div>
                  <div className="crop-paste-hint">
                    <FontAwesomeIcon icon={faClipboard} style={{ marginRight: '6px' }} />
                    Dica: você pode colar uma imagem com Ctrl+V / Cmd+V
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={handleCropCancel} className="btn btn-secondary" disabled={uploadingLogo}>
                    <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: '6px' }} />
                    Cancelar
                  </button>
                  <button onClick={handleCropConfirm} className="btn btn-primary" disabled={uploadingLogo}>
                    {uploadingLogo ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
                        Confirmar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalReimbursements = reimbursements?.length || 0;
  const pendingReimbursements = reimbursements?.filter(r => r.status === 'pending').length || 0;
  const approvedReimbursementsCount = reimbursements?.filter(r => r.status === 'approved').length || 0;
  const totalAdjustments = yearlyBalance.adjustments?.length || 0;

  // Admin view - full details
  return (
    <div className="ministry-detail-page">
      <div className="container">
        <div className="ministry-header">
          <div className="ministry-header-title">
            <div className="logo-upload-wrapper" onClick={handleLogoClick} title="Clique para alterar o logo do ministério">
              {uploadingLogo ? (
                <span className="ministry-detail-logo-fallback logo-uploading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </span>
              ) : ministry.logoUrl ? (
                <img src={ministry.logoUrl} alt="" className="ministry-detail-logo" />
              ) : (
                <span className="ministry-detail-logo-fallback">
                  <i className="fas fa-church"></i>
                </span>
              )}
              <div className="logo-upload-overlay">
                <FontAwesomeIcon icon={faCamera} />
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={handleLogoFileChange}
                style={{ display: 'none' }}
              />
            </div>
            <div>
              <h1>
                <FontAwesomeIcon icon={faWallet} />
                {ministry.name} - {selectedYear}
              </h1>
              <p className="ministry-leader-info">
                <FontAwesomeIcon icon={faCheckCircle} />
                Líder: {ministry.leader.name} • CPF/CNPJ: {ministry.leader.cpfCnpj}
              </p>
            </div>
          </div>
          <div className={`ministry-total-balance ${yearlyBalance.currentBalance < 0 ? 'text-negative' : 'text-positive'}`}>
            <FontAwesomeIcon icon={faWallet} />
            {formatAccountingCurrency(yearlyBalance.currentBalance)}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon blue">
              <FontAwesomeIcon icon={faFileInvoiceDollar} />
            </div>
            <div className="stat-label">Total de Reembolsos</div>
            <div className="stat-value">{totalReimbursements}</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon orange">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="stat-label">Pendentes</div>
            <div className="stat-value">{pendingReimbursements}</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="stat-label">Aprovados</div>
            <div className="stat-value">{approvedReimbursementsCount}</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon blue">
              <FontAwesomeIcon icon={faMoneyBillWave} />
            </div>
            <div className="stat-label">Ajustes</div>
            <div className="stat-value">{totalAdjustments}</div>
          </div>
        </div>

        {/* Patrimônio Card - Admin */}
        <Link to={`/ministry/${id}/assets`} className="patrimonio-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <div className="patrimonio-card-icon">
            <FontAwesomeIcon icon={faBoxes} />
          </div>
          <div className="patrimonio-card-info">
            <h3>Patrimônio</h3>
            <p>{assetInfo.count} {assetInfo.count === 1 ? 'bem cadastrado' : 'bens cadastrados'}</p>
          </div>
          {assetInfo.pendingCount > 0 && (
            <div className="patrimonio-card-badge">
              <FontAwesomeIcon icon={faBell} style={{ marginRight: '4px' }} />
              {assetInfo.pendingCount} pendente{assetInfo.pendingCount > 1 ? 's' : ''}
            </div>
          )}
          <FontAwesomeIcon icon={faArrowRight} className="patrimonio-card-arrow" />
        </Link>

        <div className="ministry-content">
          <div className="card">
            <h2>Detalhamento</h2>
            <div className="balance-details">
              <div className="balance-row">
                <span>Saldo Inicial:</span>
                <span>{formatAccountingCurrency(yearlyBalance.initialBalance)}</span>
              </div>

              {yearlyBalance.adjustments && yearlyBalance.adjustments.length > 0 && (
                <div className="adjustments-section">
                  <h3>
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                    Ajustes:
                  </h3>
                  {yearlyBalance.adjustments.map((adj, index) => (
                    <div key={index} className="balance-row adjustment-row">
                      <div className="adjustment-info">
                        <span className="adjustment-description">
                          {adj.amount > 0 ? (
                            <FontAwesomeIcon icon={faPlusCircle} style={{ color: '#059669', marginRight: '6px' }} />
                          ) : (
                            <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#dc2626', marginRight: '6px' }} />
                          )}
                          {adj.description}
                        </span>
                        <span className="adjustment-meta">
                          <FontAwesomeIcon icon={faCalendar} />
                          {formatDate(adj.date)}
                          {adj.createdBy && ` • Realizado por: ${adj.createdBy.name}`}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className={adj.amount < 0 ? 'text-negative' : 'text-positive'}>
                          {formatAccountingCurrency(adj.amount)}
                        </span>
                        <button
                          onClick={() => handleOpenDeleteModal('adjustment', adj._id, adj.description)}
                          className="btn btn-sm btn-danger"
                          title="Deletar ajuste"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {reimbursements && reimbursements.filter(r => r.status === 'approved' || r.status === 'paid').length > 0 && (
                <div className="reimbursements-section">
                  <h3>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Reembolsos Aprovados:
                  </h3>
                  {reimbursements
                    .filter(r => r.status === 'approved' || r.status === 'paid')
                    .map((reimb) => (
                      <div key={reimb._id} className="balance-row reimbursement-row">
                        <span>
                          <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '6px', color: 'var(--gray-500)' }} />
                          {reimb.description.substring(0, 50)}... ({formatDate(reimb.createdAt)})
                        </span>
                        <span className="text-negative">
                          ({formatAccountingCurrency(reimb.amount)})
                        </span>
                      </div>
                    ))}
                </div>
              )}

              <div className="balance-row balance-total">
                <strong>Saldo Atual:</strong>
                <strong className={yearlyBalance.currentBalance < 0 ? 'text-negative' : 'text-positive'}>
                  {formatAccountingCurrency(yearlyBalance.currentBalance)}
                </strong>
              </div>
            </div>

            {isCurrentYear && (
              <div className="adjustment-actions">
                {!showAdjustmentForm ? (
                  <>
                    <button 
                      onClick={() => setShowAdjustmentForm(true)}
                      className="btn btn-primary btn-sm"
                    >
                      <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '6px' }} />
                      Adicionar Fundo
                    </button>
                    <button 
                      onClick={() => {
                        setAdjustmentData({ ...adjustmentData, type: 'subtract' });
                        setShowAdjustmentForm(true);
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: '6px' }} />
                      Retirar Fundo
                    </button>
                  </>
                ) : (
                  <form onSubmit={handleAdjustment} className="adjustment-form">
                    <div className="form-group">
                      <label className="form-label">
                        {adjustmentData.type === 'add' ? 'Adicionar' : 'Retirar'} Valor
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={adjustmentData.amount}
                        onChange={(e) => setAdjustmentData({ ...adjustmentData, amount: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Descrição</label>
                      <input
                        type="text"
                        className="form-input"
                        value={adjustmentData.description}
                        onChange={(e) => setAdjustmentData({ ...adjustmentData, description: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary btn-sm">
                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
                        Confirmar
                      </button>
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowAdjustmentForm(false);
                          setAdjustmentData({ amount: '', description: '', type: 'add' });
                        }}
                        className="btn btn-secondary btn-sm"
                      >
                        <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: '6px' }} />
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {isCurrentYear && (
            <div className="card">
              <div className="reimbursement-header">
                <h2>
                  <FontAwesomeIcon icon={faFileInvoiceDollar} />
                  Reembolsos
                </h2>
                <button 
                  onClick={() => navigate(`/ministry/${id}/request-reimbursement`)}
                  className="btn btn-primary btn-sm"
                >
                  <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '6px' }} />
                  Solicitar Reembolso
                </button>
              </div>

              {reimbursements && reimbursements.length > 0 ? (
                <div className="reimbursements-list">
                  {reimbursements.map((reimb) => (
                    <div key={reimb._id} className="reimbursement-item">
                      <div className="reimbursement-info">
                        <div className="reimbursement-header-row">
                          <strong>
                            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px', color: 'var(--primary-blue)' }} />
                            {reimb.requester.name}
                          </strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className={`badge badge-${reimb.status}`}>
                              <FontAwesomeIcon 
                                icon={
                                  reimb.status === 'pending' ? faClock :
                                  reimb.status === 'approved' ? faCheckCircle :
                                  reimb.status === 'rejected' ? faTimesCircle :
                                  faCheckCircle
                                } 
                                style={{ marginRight: '4px' }} 
                              />
                              {reimb.status === 'pending' && 'Pendente'}
                              {reimb.status === 'approved' && 'Aprovado'}
                              {reimb.status === 'rejected' && 'Reprovado'}
                              {reimb.status === 'paid' && 'Pago'}
                            </span>
                            <button
                              onClick={() => handleOpenDeleteModal('reimbursement', reimb._id, reimb.description)}
                              className="btn btn-sm btn-danger"
                              title="Deletar reembolso"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                        <p className="reimbursement-description">{reimb.description}</p>
                        <div className="reimbursement-details">
                          <span>
                            <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '4px' }} />
                            Valor: {formatAccountingCurrency(reimb.amount)}
                          </span>
                          <span>
                            <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '4px' }} />
                            Data: {formatDate(reimb.createdAt)}
                          </span>
                        </div>
                        {reimb.status === 'paid' && reimb.paymentProofUrl && (
                          <div className="payment-proof-info">
                            <a
                              href={reimb.paymentProofUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-success"
                              style={{ marginTop: '8px' }}
                            >
                              <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '6px' }} />
                              Ver Comprovante de Pagamento
                            </a>
                          </div>
                        )}
                        {reimb.rejectionReason && (
                          <div className="rejection-reason">
                            <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '6px' }} />
                            <strong>Motivo da reprovação:</strong> {reimb.rejectionReason}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center">Nenhum reembolso solicitado ainda.</p>
              )}
            </div>
          )}
        </div>

        {showDeleteModal && deleteTarget && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                  Deletar {deleteTarget.type === 'adjustment' ? 'Ajuste' : 'Reembolso'}
                </h2>
                <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja deletar este {deleteTarget.type === 'adjustment' ? 'ajuste' : 'reembolso'}?</p>
                <div className="alert alert-info" style={{ marginTop: '12px' }}>
                  <strong>Descrição:</strong> {deleteTarget.description}
                </div>
                {deleteTarget.type === 'reimbursement' && (
                  <div className="alert alert-danger" style={{ marginTop: '12px' }}>
                    <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '6px' }} />
                    O arquivo anexado também será removido do servidor.
                  </div>
                )}
                {deleteTarget.type === 'adjustment' && (
                  <div className="alert alert-info" style={{ marginTop: '12px' }}>
                    O saldo do ministério será recalculado automaticamente.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger"
                >
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} />
                  Deletar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Crop Modal */}
        {showCropModal && imageSrc && (
          <div className="modal-overlay crop-modal-overlay" onClick={handleCropCancel}>
            <div className="modal-content crop-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={faCropAlt} style={{ marginRight: '8px' }} />
                  Ajustar Logo
                </h2>
                <button className="modal-close" onClick={handleCropCancel}>×</button>
              </div>
              <div className="modal-body crop-modal-body">
                <div className="crop-hint">
                  <FontAwesomeIcon icon={faCamera} style={{ marginRight: '6px' }} />
                  Arraste para posicionar e use o zoom para ajustar
                </div>
                <div className="crop-container">
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                <div className="crop-controls">
                  <FontAwesomeIcon icon={faSearchMinus} className="crop-zoom-icon" />
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.05}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="crop-zoom-slider"
                  />
                  <FontAwesomeIcon icon={faSearchPlus} className="crop-zoom-icon" />
                </div>
                <div className="crop-paste-hint">
                  <FontAwesomeIcon icon={faClipboard} style={{ marginRight: '6px' }} />
                  Dica: você pode colar uma imagem com Ctrl+V / Cmd+V
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={handleCropCancel} className="btn btn-secondary" disabled={uploadingLogo}>
                  <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: '6px' }} />
                  Cancelar
                </button>
                <button onClick={handleCropConfirm} className="btn btn-primary" disabled={uploadingLogo}>
                  {uploadingLogo ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MinistryDetail;
